import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { day, money, parseMoney } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import type { Catalog } from '../../lib/types.ts';
import { useAction, useSave } from './common.tsx';

type Kind = 'percent' | 'amount' | 'buy_get';
interface Promo {
  id: string;
  name: string;
  kind: Kind;
  percent: number;
  amount: number;
  product: string | null;
  product_name: string | null;
  buy_qty: number;
  free_qty: number;
  min_total: number;
  code: string | null;
  one_time_codes: boolean;
  starts_on: string | null;
  ends_on: string | null;
  max_uses: number | null;
  uses: number;
  active: boolean;
  note: string | null;
  given: number;
  codes: number;
  codes_used: number;
}
interface Data {
  today: string;
  promotions: Promo[];
}
interface Coupon {
  id: string;
  code: string;
  used: boolean;
  used_at: string | null;
  order_name: string | null;
}

/** What a promotion gives, in one line: "10% off", "500 L off", "Buy 2 Espresso, get 1 free". */
export function promoText(p: Pick<Promo, 'kind' | 'percent' | 'amount' | 'buy_qty' | 'free_qty' | 'product_name'>) {
  if (p.kind === 'percent') return t('promo.percent_off', { n: p.percent });
  if (p.kind === 'amount') return t('promo.amount_off', { amount: money(p.amount) });
  return t('promo.buy_get', { buy: p.buy_qty, free: p.free_qty, item: p.product_name ?? '' });
}

function status(p: Promo, today: string): { tone: 'ok' | 'neutral' | 'warn'; label: string } {
  if (!p.active) return { tone: 'neutral', label: t('promo.off') };
  if (p.starts_on && today < p.starts_on) return { tone: 'warn', label: t('promo.starts', { date: day(p.starts_on) }) };
  if (p.ends_on && today > p.ends_on) return { tone: 'neutral', label: t('promo.ended') };
  if (p.max_uses !== null && p.uses >= p.max_uses) return { tone: 'neutral', label: t('promo.used_up') };
  return { tone: 'ok', label: t('promo.running') };
}

/**
 * Loyalty → Promotions: discounts the till offers on its own, codes customers bring (one shared
 * code, or one-time codes printed on flyers), with how often each was used and what it gave away.
 */
export function Promotions() {
  const { can } = useSession();
  const manage = can('loyalty.manage');
  const { data, reload } = useAction<Data>('loyalty.promotions');
  const [editing, setEditing] = useState<Promo | 'new' | null>(null);
  const [codes, setCodes] = useState<Promo | null>(null);
  const { busy, run } = useSave();
  const toggle = (p: Promo, active: boolean) =>
    void run(
      async () => {
        await action('loyalty.set_promotion_active', { id: p.id, active });
        await reload();
      },
      active ? t('promo.turned_on') : t('promo.turned_off'),
    );

  return (
    <section className="promo" aria-label={t('loyalty.tab_promos')}>
      <div className="promo-head">
        <p className="muted">{t('promo.intro')}</p>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            {t('promo.new')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : !data.promotions.length ? (
        <EmptyState icon="percent" title={t('promo.empty')} body={t('promo.empty_body')} />
      ) : (
        <div className="list-card" role="list">
          {data.promotions.map((p) => {
            const s = status(p, data.today);
            return (
              <div key={p.id} role="listitem" className={`list-row promo-row${p.active ? '' : ' archived'}`}>
                <span className="list-icon" aria-hidden="true">
                  <Icon name={p.kind === 'buy_get' ? 'gift' : 'percent'} size={18} />
                </span>
                <button className="list-main" onClick={() => manage && setEditing(p)} disabled={!manage}>
                  <span className="list-title">{p.name}</span>
                  <span className="list-sub">
                    {[
                      promoText(p),
                      p.min_total ? t('promo.min', { amount: money(p.min_total) }) : null,
                      p.code ? t('promo.code_is', { code: p.code }) : p.one_time_codes ? tn('promo.codes_n', p.codes, { used: p.codes_used }) : t('promo.at_till'),
                      p.starts_on || p.ends_on ? `${p.starts_on ? day(p.starts_on) : '…'} – ${p.ends_on ? day(p.ends_on) : '…'}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
                <span className="promo-end">
                  <span className="promo-uses">
                    <span className="tabular">{p.max_uses ? t('promo.uses_of', { n: p.uses, max: p.max_uses }) : tn('promo.uses', p.uses)}</span>
                    <span className="muted small">{t('promo.given', { amount: money(p.given) })}</span>
                  </span>
                  <StatusBadge tone={s.tone} icon={s.tone === 'ok' ? 'checkCircle' : s.tone === 'warn' ? 'clock' : 'pause'} label={s.label} />
                  {p.one_time_codes && manage ? (
                    <Button size="sm" variant="ghost" icon="ticket" onClick={() => setCodes(p)}>
                      {t('promo.codes')}
                    </Button>
                  ) : null}
                  {manage ? (
                    <Switch checked={p.active} label={p.active ? t('promo.turn_off', { name: p.name }) : t('promo.turn_on', { name: p.name })} onChange={(v) => toggle(p, v)} disabled={busy} />
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {editing ? (
        <PromoDialog
          promo={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
      {codes ? <CodesDialog promo={codes} onClose={() => (setCodes(null), void reload())} /> : null}
    </section>
  );
}

function PromoDialog({ promo, onClose, onSaved }: { promo: Promo | null; onClose: () => void; onSaved: () => void }) {
  const { data: catalog } = useAction<Catalog>('products.catalog', { include_inactive: false });
  const { busy, run } = useSave();
  const [tried, setTried] = useState(false);
  const [name, setName] = useState(promo?.name ?? '');
  const [kind, setKind] = useState<Kind>(promo?.kind ?? 'percent');
  const [percent, setPercent] = useState(promo?.kind === 'percent' ? String(promo.percent) : '10');
  const [amount, setAmount] = useState(promo?.kind === 'amount' ? (promo.amount / 100).toFixed(2).replace(/\.00$/, '') : '');
  const [product, setProduct] = useState(promo?.product ?? '');
  const [buy, setBuy] = useState(String(promo?.buy_qty || 2));
  const [free, setFree] = useState(String(promo?.free_qty || 1));
  const [min, setMin] = useState(promo?.min_total ? (promo.min_total / 100).toFixed(2).replace(/\.00$/, '') : '');
  const [how, setHow] = useState<'till' | 'code' | 'codes'>(promo?.one_time_codes ? 'codes' : promo?.code ? 'code' : 'till');
  const [code, setCode] = useState(promo?.code ?? '');
  const [from, setFrom] = useState(promo?.starts_on ?? '');
  const [to, setTo] = useState(promo?.ends_on ?? '');
  const [max, setMax] = useState(promo?.max_uses ? String(promo.max_uses) : '');
  const minor = parseMoney(amount);
  const minMinor = min.trim() ? parseMoney(min) : 0;
  const errors = {
    name: name.trim() ? null : t('form.required'),
    value:
      kind === 'percent' && !(Number(percent) >= 1 && Number(percent) <= 100)
        ? t('promo.e_percent')
        : kind === 'amount' && !(minor && minor > 0)
          ? t('promo.e_amount')
          : kind === 'buy_get' && (!product || !(Number(buy) >= 1) || !(Number(free) >= 1))
            ? t('promo.e_buy_get')
            : null,
    min: minMinor === null || minMinor < 0 ? t('promo.e_amount') : null,
    code: how === 'code' && !/^[A-Za-z0-9-]{3,40}$/.test(code.trim()) ? t('promo.e_code') : null,
    dates: from && to && to < from ? t('promo.e_dates') : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const productName = catalog?.products.find((p) => p.id === product)?.name ?? t('promo.the_item');
  const preview = promoText({ kind, percent: Number(percent) || 0, amount: minor ?? 0, buy_qty: Number(buy) || 0, free_qty: Number(free) || 0, product_name: productName });

  const save = () => {
    setTried(true);
    if (invalid) return;
    void run(async () => {
      await action('loyalty.save_promotion', {
        ...(promo ? { id: promo.id } : {}),
        name: name.trim(),
        kind,
        percent: Number(percent) || 0,
        amount: minor ?? 0,
        product: kind === 'buy_get' ? product : null,
        buy_qty: Number(buy) || 0,
        free_qty: Number(free) || 0,
        min_total: minMinor ?? 0,
        code: how === 'code' ? code.trim() : null,
        one_time_codes: how === 'codes',
        starts_on: from || null,
        ends_on: to || null,
        max_uses: max ? Number(max) : null,
      });
      onSaved();
    }, t('promo.saved'));
  };

  return (
    <Dialog
      size="lg"
      title={promo ? t('promo.edit') : t('promo.new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={save} busy={busy}>
            {t('promo.save')}
          </Button>
        </>
      }
    >
      <Field label={t('promo.name')} error={tried ? errors.name : null} required>
        <input className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('promo.name_ph')} data-autofocus />
      </Field>
      <div className="field">
        <span className="field-label" id="promo-kind">
          {t('promo.kind')}
        </span>
        <div className="segmented" role="radiogroup" aria-labelledby="promo-kind">
          {(['percent', 'amount', 'buy_get'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              <Icon name={k === 'buy_get' ? 'gift' : 'percent'} size={16} />
              {t(`promo.k_${k}`)}
            </button>
          ))}
        </div>
      </div>
      {kind === 'percent' ? (
        <Field label={t('promo.percent')} error={tried ? errors.value : null}>
          <input className="input tabular" inputMode="numeric" value={percent} onChange={(e) => setPercent(e.target.value.replace(/\D/g, ''))} />
        </Field>
      ) : kind === 'amount' ? (
        <Field label={t('promo.amount')} error={tried ? errors.value : null}>
          <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      ) : (
        <div className="promo-three">
          <Field label={t('promo.item')} error={tried ? errors.value : null}>
            <select className="input" value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="">{t('promo.choose_item')}</option>
              {catalog?.products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('promo.buy')}>
            <input className="input tabular" inputMode="numeric" value={buy} onChange={(e) => setBuy(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label={t('promo.free')}>
            <input className="input tabular" inputMode="numeric" value={free} onChange={(e) => setFree(e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>
      )}
      <Field label={t('promo.min_total')} hint={t('promo.min_total_hint')} error={tried ? errors.min : null}>
        <input className="input tabular" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} placeholder="0" />
      </Field>
      <div className="field">
        <span className="field-label" id="promo-how">
          {t('promo.how')}
        </span>
        <div className="segmented" role="radiogroup" aria-labelledby="promo-how">
          {(['till', 'code', 'codes'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={how === k} className={how === k ? 'active' : ''} onClick={() => setHow(k)}>
              {t(`promo.how_${k}`)}
            </button>
          ))}
        </div>
        <span className="field-hint">{t(`promo.how_${how}_hint`)}</span>
      </div>
      {how === 'code' ? (
        <Field label={t('promo.code')} error={tried ? errors.code : null}>
          <input className="input mono" maxLength={40} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SUMMER10" />
        </Field>
      ) : null}
      <div className="promo-three">
        <Field label={t('promo.from')}>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('promo.to')} error={tried ? errors.dates : null}>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field label={t('promo.max_uses')} hint={t('promo.max_uses_hint')}>
          <input className="input tabular" inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))} />
        </Field>
      </div>
      <p className="au-preview">
        <Icon name="percent" size={16} />
        <span>
          <b>{preview}</b>
          {minMinor ? ` · ${t('promo.min', { amount: money(minMinor) })}` : ''}
          {' · '}
          {how === 'code' ? t('promo.code_is', { code: code.trim().toUpperCase() || '…' }) : how === 'codes' ? t('promo.how_codes') : t('promo.at_till')}
        </span>
      </p>
    </Dialog>
  );
}

function CodesDialog({ promo, onClose }: { promo: Promo; onClose: () => void }) {
  const { data, reload } = useAction<Coupon[]>('loyalty.coupons', { promotion: promo.id });
  const { busy, run } = useSave();
  const [count, setCount] = useState('20');
  const [prefix, setPrefix] = useState('');
  const make = () =>
    void run(async () => {
      await action('loyalty.make_coupons', { promotion: promo.id, count: Math.min(1000, Math.max(1, Number(count) || 1)), prefix: prefix.trim() });
      await reload();
    }, t('promo.codes_made'));
  const download = () => {
    const rows = ['code,used,bill', ...(data ?? []).map((c) => `${c.code},${c.used ? 'yes' : 'no'},${c.order_name ?? ''}`)];
    const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${promo.name.replace(/[^\w-]+/g, '-')}-codes.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const free = data?.filter((c) => !c.used).length ?? 0;
  return (
    <Dialog
      title={t('promo.codes_for', { name: promo.name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" icon="download" onClick={download} disabled={!data?.length}>
            {t('promo.download')}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      <div className="promo-make">
        <Field label={t('promo.how_many')}>
          <input className="input tabular" inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label={t('promo.prefix')} hint={t('promo.prefix_hint')}>
          <input className="input mono" maxLength={10} value={prefix} onChange={(e) => setPrefix(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} />
        </Field>
        <div className="promo-make-btn">
          <Button icon="plus" onClick={make} busy={busy}>
            {t('promo.make')}
          </Button>
        </div>
      </div>
      {!data ? (
        <Skeleton h={120} />
      ) : data.length ? (
        <>
          <p className="muted small">{t('promo.codes_left', { free, total: data.length })}</p>
          <ul className="promo-codes">
            {data.map((c) => (
              <li key={c.id} className={c.used ? 'used' : ''}>
                <span className="mono">{c.code}</span>
                {c.used ? <span className="muted small">{c.order_name ?? t('promo.used')}</span> : null}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="muted">{t('promo.no_codes')}</p>
      )}
    </Dialog>
  );
}
